// src/components/contracts/AdminUploadedDocumentsViewer.jsx - FIXED
import React, { useState, useEffect } from 'react';
import {
  FileText,
  Image as ImageIcon,
  Archive,
  File,
  Download,
  Eye,
  X,
  Loader2,
  AlertCircle,
  CheckCircle,
  RefreshCw,
  ZoomIn,
  ZoomOut,
  RotateCw,
  Maximize2,
  Minimize2,
  FolderOpen,
  FileSignature,
  Shield
} from 'lucide-react';
import toast from 'react-hot-toast';
import api from '../../services/api';

/**
 * Componentă unificată pentru vizualizarea tuturor documentelor semnate
 * Afișează documente de la: Contract, Acte Adiționale, Notificări de Reziliere
 */
const AdminUploadedDocumentsViewer = ({ contractId, contractNumber, additionalActs = [], notices = [] }) => {
  const [allDocuments, setAllDocuments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [previewFile, setPreviewFile] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewError, setPreviewError] = useState(false);
  const [blobUrl, setBlobUrl] = useState(null);

  // Preview controls
  const [zoom, setZoom] = useState(100);
  const [rotation, setRotation] = useState(0);
  const [isFullscreen, setIsFullscreen] = useState(false);

  // Filter state
  const [filter, setFilter] = useState('all'); // 'all', 'contract', 'acts', 'notices'

  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(5);

  useEffect(() => {
    if (contractId) {
      loadAllDocuments();
    }
  }, [contractId, additionalActs, notices]);

  // Cleanup blob URL
  useEffect(() => {
    return () => {
      if (blobUrl) {
        URL.revokeObjectURL(blobUrl);
      }
    };
  }, [blobUrl]);

  const loadAllDocuments = async () => {
    setLoading(true);
    setError(null);

    try {
      const documentsPromises = [];

      // 1. Load Contract Documents
      documentsPromises.push(
        api.get(`/contracts/${contractId}/signed-documents`)
          .then(response => ({
            type: 'contract',
            icon: Shield,
            color: 'blue',
            label: 'Contract',
            documents: response.data?.files || response.data || []
          }))
          .catch(err => {
            if (err.response?.status === 404) {
              return { type: 'contract', icon: Shield, color: 'blue', label: 'Contract', documents: [] };
            }
            throw err;
          })
      );

      // 2. Load Additional Acts Documents
      console.log(additionalActs);
      if (additionalActs && additionalActs.length > 0) {
        additionalActs.forEach(act => {
          documentsPromises.push(
            api.get(`/additionalacts/${act.id}/signed-documents`)
              .then(response => ({
                type: 'additional-act',
                icon: FileSignature,
                color: 'purple',
                label: `Act Adițional ${act.number}`,
                actId: act.id,
                actNumber: act.number,
                documents: response.data?.files || response.data || []
              }))
              .catch(err => {
                if (err.response?.status === 404) {
                  return {
                    type: 'additional-act',
                    icon: FileSignature,
                    color: 'purple',
                    label: `Act Adițional ${act.number}`,
                    actId: act.id,
                    actNumber: act.number,
                    documents: []
                  };
                }
                throw err;
              })
          );
        });
      }
      console.log(notices);
      // 3. Load Notices Documents
      if (notices && notices.length > 0) {
        notices.forEach(notice => {
          documentsPromises.push(
            api.get(`/notices/${notice.id}/signed-documents`)
              .then(response => ({
                type: 'notice',
                icon: AlertCircle,
                color: 'red',
                label: `Notificare de Reziliere #${notice.id}`,
                noticeId: notice.id,
                documents: response.data?.files || response.data || []
              }))
              .catch(err => {
                if (err.response?.status === 404) {
                  return {
                    type: 'notice',
                    icon: AlertCircle,
                    color: 'red',
                    label: `Notificare de Reziliere #${notice.id}`,
                    noticeId: notice.id,
                    documents: []
                  };
                }
                throw err;
              })
          );
        });
      }

      const results = await Promise.all(documentsPromises);
      console.log("results:", results);
      // Flatten and tag each document with its source
      const allDocs = results.flatMap(source =>
        (source.documents || []).map(doc => ({
          ...doc,
          // ✅ FIX: Ensure fileName exists
          fileName: doc.fileName || doc.name || 'unknown_file',
          sourceType: source.type,
          sourceLabel: source.label,
          sourceIcon: source.icon,
          sourceColor: source.color,
          sourceId: source.actId || source.noticeId || contractId
        }))
      );

      console.log('Loaded documents:', allDocs); // Debug log
      setAllDocuments(allDocs);
    } catch (err) {
      console.error('Error loading documents:', err);
      setError('Eroare la încărcarea documentelor');
      toast.error('Eroare la încărcarea documentelor');
    } finally {
      setLoading(false);
    }
  };

  // ✅ FIX: Add null check for fileName
  const getFileIcon = (fileName) => {
    if (!fileName) return <File className="w-5 h-5 text-gray-500" />;

    const ext = fileName.toLowerCase().split('.').pop();
    const iconClass = "w-5 h-5";

    if (['jpg', 'jpeg', 'png', 'gif', 'webp'].includes(ext)) {
      return <ImageIcon className={`${iconClass} text-blue-500`} />;
    }
    if (['zip', '7z', 'rar'].includes(ext)) {
      return <Archive className={`${iconClass} text-orange-500`} />;
    }
    if (['pdf'].includes(ext)) {
      return <FileText className={`${iconClass} text-red-500`} />;
    }
    if (['doc', 'docx'].includes(ext)) {
      return <FileText className={`${iconClass} text-blue-600`} />;
    }
    return <File className={`${iconClass} text-gray-500`} />;
  };

  // ✅ FIX: Add null check
  const isImage = (fileName) => {
    if (!fileName) return false;
    const ext = fileName.toLowerCase().split('.').pop();
    return ['jpg', 'jpeg', 'png', 'gif', 'webp'].includes(ext);
  };

  // ✅ FIX: Add null check
  const isPDF = (fileName) => {
    if (!fileName) return false;
    const ext = fileName.toLowerCase().split('.').pop();
    return ext === 'pdf';
  };

  const canPreview = (fileName) => {
    return isImage(fileName) || isPDF(fileName);
  };

  const getDownloadEndpoint = (doc) => {
    switch (doc.sourceType) {
      case 'additional-act':
        return `/additionalacts/${doc.sourceId}/download-signed-file`;
      case 'notice':
        return `/notices/${doc.sourceId}/download-signed-file`;
      case 'contract':
      default:
        return `/contracts/${contractId}/download-signed-file`;
    }
  };

  const handleDownload = async (doc) => {
    try {
      const endpoint = getDownloadEndpoint(doc);

      const response = await api.get(endpoint, {
        params: { path: doc.filePath },
        responseType: 'blob'
      });

      if (!response.data) {
        throw new Error('No data received');
      }

      const blob = response.data;
      const url = URL.createObjectURL(blob);

      const link = document.createElement('a');
      link.href = url;
      link.download = doc.fileName || 'document';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      URL.revokeObjectURL(url);

      toast.success('Fișier descărcat cu succes');
    } catch (err) {
      console.error('Error downloading file:', err);
      toast.error('Eroare la descărcarea fișierului');
    }
  };

  const handlePreview = async (doc) => {
    setPreviewLoading(true);
    setPreviewError(false);

    try {
      if (blobUrl) {
        URL.revokeObjectURL(blobUrl);
        setBlobUrl(null);
      }

      if (isImage(doc.fileName) || isPDF(doc.fileName)) {
        const endpoint = getDownloadEndpoint(doc);

        const response = await api.get(endpoint, {
          params: { path: doc.filePath },
          responseType: 'blob'
        });

        if (!response.data) {
          throw new Error('No data received');
        }

        const blob = response.data;
        const url = URL.createObjectURL(blob);

        setBlobUrl(url);
        setPreviewFile({
          ...doc,
          url: url,
          type: isImage(doc.fileName) ? 'image' : 'pdf'
        });

        setZoom(100);
        setRotation(0);
      }
    } catch (err) {
      console.error('Error previewing file:', err);
      setPreviewError(true);
      toast.error('Eroare la previzualizarea fișierului');
    } finally {
      setPreviewLoading(false);
    }
  };

  const closePreview = () => {
    if (blobUrl) {
      URL.revokeObjectURL(blobUrl);
      setBlobUrl(null);
    }
    setPreviewFile(null);
    setPreviewError(false);
    setZoom(100);
    setRotation(0);
    setIsFullscreen(false);
  };

  const handleZoomIn = () => setZoom(prev => Math.min(prev + 25, 300));
  const handleZoomOut = () => setZoom(prev => Math.max(prev - 25, 25));
  const handleRotate = () => setRotation(prev => (prev + 90) % 360);
  const handleReset = () => { setZoom(100); setRotation(0); };
  const toggleFullscreen = () => setIsFullscreen(prev => !prev);

  // Filter documents
  const filteredDocuments = allDocuments.filter(doc => {
    if (filter === 'all') return true;
    if (filter === 'contract') return doc.sourceType === 'contract';
    if (filter === 'acts') return doc.sourceType === 'additional-act';
    if (filter === 'notices') return doc.sourceType === 'notice';
    return true;
  });

  const totalPages = Math.ceil(filteredDocuments.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const paginatedDocuments = filteredDocuments.slice(startIndex, endIndex);

  // Reset to page 1 when filter changes
  useEffect(() => {
    setCurrentPage(1);
  }, [filter]);

  // Get color classes for source badge
  const getSourceColorClasses = (color) => {
    const colorMap = {
      blue: 'bg-blue-100 text-blue-800 border-blue-200',
      purple: 'bg-purple-100 text-purple-800 border-purple-200',
      red: 'bg-red-100 text-red-800 border-red-200'
    };
    return colorMap[color] || colorMap.blue;
  };

  // Count by type
  const counts = {
    total: allDocuments.length,
    contract: allDocuments.filter(d => d.sourceType === 'contract').length,
    acts: allDocuments.filter(d => d.sourceType === 'additional-act').length,
    notices: allDocuments.filter(d => d.sourceType === 'notice').length
  };

  if (loading) {
    return (
      <div className="bg-white rounded-lg shadow-md p-6">
        <div className="flex items-center justify-center py-8">
          <Loader2 className="w-8 h-8 text-blue-500 animate-spin" />
          <span className="ml-3 text-gray-600">Se încarcă documentele...</span>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-white rounded-lg shadow-md p-6">
        <div className="flex items-center gap-3 text-red-600 mb-4">
          <AlertCircle className="w-6 h-6" />
          <div>
            <p className="font-medium">Eroare</p>
            <p className="text-sm text-red-500">{error}</p>
          </div>
        </div>
        <button
          onClick={loadAllDocuments}
          className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors flex items-center gap-2"
        >
          <RefreshCw className="w-4 h-4" />
          Încearcă din nou
        </button>
      </div>
    );
  }

  if (allDocuments.length === 0) {
    return (
      <div className="bg-white rounded-lg shadow-md p-6">
        <div className="text-center py-8">
          <FolderOpen className="w-12 h-12 text-gray-400 mx-auto mb-3" />
          <p className="text-gray-600 font-medium">Nu există documente semnate încărcate</p>
          <p className="text-sm text-gray-500 mt-1">
            Clientul nu a încărcat încă documente semnate
          </p>
        </div>
      </div>
    );
  }

  return (
    <>
      <div className="bg-white rounded-lg shadow-md p-6">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-2">
            <CheckCircle className="w-6 h-6 text-green-600" />
            <h3 className="text-lg font-semibold text-gray-900">
              Toate Documentele Semnate
            </h3>
          </div>
          <button
            onClick={loadAllDocuments}
            className="p-2 text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-lg transition-colors"
            title="Reîncarcă lista"
          >
            <RefreshCw className="w-5 h-5" />
          </button>
        </div>

        {/* Filters */}
        <div className="mb-6 flex items-center gap-3 flex-wrap">
          <button
            onClick={() => setFilter('all')}
            className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${filter === 'all'
              ? 'bg-blue-600 text-white'
              : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
          >
            Toate ({counts.total})
          </button>
          <button
            onClick={() => setFilter('contract')}
            className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${filter === 'contract'
              ? 'bg-blue-600 text-white'
              : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
          >
            <Shield className="w-4 h-4 inline mr-1" />
            Contract ({counts.contract})
          </button>
          {counts.acts > 0 && (
            <button
              onClick={() => setFilter('acts')}
              className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${filter === 'acts'
                ? 'bg-purple-600 text-white'
                : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
            >
              <FileSignature className="w-4 h-4 inline mr-1" />
              Acte Adiționale ({counts.acts})
            </button>
          )}
          {counts.notices > 0 && (
            <button
              onClick={() => setFilter('notices')}
              className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${filter === 'notices'
                ? 'bg-red-600 text-white'
                : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
            >
              <AlertCircle className="w-4 h-4 inline mr-1" />
              Rezilieri ({counts.notices})
            </button>
          )}
        </div>

        {/* Summary */}
        <div className="mb-4 flex items-center justify-between">
          <p className="text-sm text-gray-600">
            Afișate: <span className="font-semibold">{filteredDocuments.length}</span> din {allDocuments.length} documente
          </p>
          {contractNumber && (
            <p className="text-sm text-gray-500">
              Contract: <span className="font-medium">{contractNumber}</span>
            </p>
          )}
        </div>

        {/* Documents List */}
        <div className="space-y-2">
          {paginatedDocuments.length === 0 ? (
            <div className="text-center py-8 text-gray-500">
              <FolderOpen className="w-10 h-10 mx-auto mb-2 opacity-50" />
              <p>Niciun document pentru filtrul selectat</p>
            </div>
          ) : (
            paginatedDocuments.map((doc, index) => {
              const SourceIcon = doc.sourceIcon;

              return (
                <div
                  key={`${doc.sourceType}-${doc.sourceId}-${index}`}
                  className="flex items-center gap-3 p-4 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors border border-gray-200"
                >
                  {/* File Icon */}
                  <div className="flex-shrink-0">
                    {getFileIcon(doc.fileName)}
                  </div>

                  {/* File Info */}
                  <div className="flex-1 min-w-0">
                    <p className="font-medium text-gray-900 truncate">
                      {doc.fileName || 'Fișier necunoscut'}
                    </p>
                    <div className="flex items-center gap-2 mt-1">
                      <span className="text-xs text-gray-500">
                        {doc.fileExtension?.toUpperCase() || 'FILE'}
                      </span>
                      {/* Source Badge */}
                      <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium border ${getSourceColorClasses(doc.sourceColor)}`}>
                        <SourceIcon className="w-3 h-3 mr-1" />
                        {doc.sourceLabel}
                      </span>
                    </div>
                  </div>

                  {/* Actions */}
                  <div className="flex items-center gap-2">
                    {canPreview(doc.fileName) && (
                      <button
                        onClick={() => handlePreview(doc)}
                        className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                        title="Previzualizare"
                      >
                        <Eye className="w-5 h-5" />
                      </button>
                    )}
                    <button
                      onClick={() => handleDownload(doc)}
                      className="p-2 text-gray-600 hover:bg-gray-200 rounded-lg transition-colors"
                      title="Descarcă"
                    >
                      <Download className="w-5 h-5" />
                    </button>
                  </div>
                </div>
              );
            })
          )}
        </div>

        {/* Pagination Controls */}
        {totalPages > 1 && (
          <div className="mt-6 flex items-center justify-between border-t pt-4">
            <div className="text-sm text-gray-600">
              Afișate {startIndex + 1}-{Math.min(endIndex, filteredDocuments.length)} din {filteredDocuments.length}
            </div>

            <div className="flex items-center gap-2">
              <button
                onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                disabled={currentPage === 1}
                className="px-3 py-2 border rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                Anterior
              </button>

              <div className="flex items-center gap-1">
                {[...Array(totalPages)].map((_, i) => {
                  const page = i + 1;
                  // Show first, last, current, and adjacent pages
                  if (
                    page === 1 ||
                    page === totalPages ||
                    (page >= currentPage - 1 && page <= currentPage + 1)
                  ) {
                    return (
                      <button
                        key={page}
                        onClick={() => setCurrentPage(page)}
                        className={`px-3 py-2 rounded-lg transition-colors ${currentPage === page
                          ? 'bg-blue-600 text-white'
                          : 'hover:bg-gray-100'
                          }`}
                      >
                        {page}
                      </button>
                    );
                  } else if (page === currentPage - 2 || page === currentPage + 2) {
                    return <span key={page} className="px-2">...</span>;
                  }
                  return null;
                })}
              </div>

              <button
                onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
                disabled={currentPage === totalPages}
                className="px-3 py-2 border rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                Următor
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Preview Modal - Same as before, no changes needed */}
      {previewFile && (
        <div className="fixed inset-0 z-50 bg-gray-900">
          {/* Modal Header */}
          <div className="flex items-center justify-between p-4 bg-white border-b">
            <div className="flex items-center space-x-4">
              <div className="flex items-center gap-2">
                {getFileIcon(previewFile.filename)}
                <div>
                  <h3 className="font-semibold text-gray-900">
                    {previewFile.filename}
                  </h3>
                  <div className="flex items-center gap-2 mt-1">
                    <span className="text-sm text-gray-500">
                      {previewFile.fileExtension?.toUpperCase()}
                    </span>
                    {previewFile.sourceLabel && (
                      <>
                        <span className="text-gray-300">•</span>
                        <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium border ${getSourceColorClasses(previewFile.sourceColor)}`}>
                          <previewFile.sourceIcon className="w-3 h-3 mr-1" />
                          {previewFile.sourceLabel}
                        </span>
                      </>
                    )}
                  </div>
                </div>
              </div>
            </div>

            <div className="flex items-center space-x-2">
              {/* Zoom Controls */}
              <div className="flex items-center space-x-1 border rounded-lg p-1">
                <button
                  onClick={handleZoomOut}
                  className="p-1 rounded hover:bg-gray-100 transition-colors"
                  disabled={zoom <= 25}
                >
                  <ZoomOut className="w-4 h-4" />
                </button>
                <span className="px-2 text-sm font-medium min-w-[3rem] text-center">
                  {zoom}%
                </span>
                <button
                  onClick={handleZoomIn}
                  className="p-1 rounded hover:bg-gray-100 transition-colors"
                  disabled={zoom >= 300}
                >
                  <ZoomIn className="w-4 h-4" />
                </button>
              </div>

              <button onClick={handleRotate} className="p-2 rounded-lg hover:bg-gray-100 transition-colors">
                <RotateCw className="w-4 h-4" />
              </button>

              <button onClick={handleReset} className="px-3 py-1 text-sm border rounded-lg hover:bg-gray-50 transition-colors">
                Reset
              </button>

              <button
                onClick={() => handleDownload(previewFile)}
                className="flex items-center px-3 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors"
              >
                <Download className="w-4 h-4 mr-2" />
                Descarcă
              </button>

              <button onClick={toggleFullscreen} className="p-2 rounded-lg hover:bg-gray-100 transition-colors">
                {isFullscreen ? <Minimize2 className="w-4 h-4" /> : <Maximize2 className="w-4 h-4" />}
              </button>

              <button onClick={closePreview} className="p-2 rounded-lg hover:bg-gray-100 transition-colors">
                <X className="w-4 h-4" />
              </button>
            </div>
          </div>

          {/* Modal Content */}
          <div className="overflow-hidden relative bg-gray-800" style={{ height: 'calc(100vh - 73px)' }}>
            {previewLoading ? (
              <div className="flex items-center justify-center h-full">
                <div className="text-center text-white">
                  <Loader2 className="w-12 h-12 animate-spin mx-auto mb-4" />
                  <p>Se încarcă fișierul...</p>
                </div>
              </div>
            ) : previewError ? (
              <div className="flex items-center justify-center h-full">
                <div className="text-center text-white max-w-md px-4">
                  <div className="w-16 h-16 bg-red-500 rounded-full flex items-center justify-center mx-auto mb-4">
                    <X className="w-8 h-8" />
                  </div>
                  <h3 className="text-lg font-semibold mb-2">Eroare la încărcarea fișierului</h3>
                  <p className="text-gray-300 mb-4">
                    Nu s-a putut încărca fișierul pentru vizualizare.
                  </p>
                  <button
                    onClick={() => handleDownload(previewFile)}
                    className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 inline-flex items-center"
                  >
                    <Download className="w-4 h-4 mr-2" />
                    Descarcă
                  </button>
                </div>
              </div>
            ) : blobUrl ? (
              <div
                className="w-full h-full flex items-center justify-center"
                style={{
                  transform: `scale(${zoom / 100}) rotate(${rotation}deg)`,
                  transition: 'transform 0.2s ease'
                }}
              >
                {previewFile.type === 'image' ? (
                  <img
                    src={blobUrl}
                    alt={previewFile.filename}
                    className="max-w-full max-h-full object-contain"
                  />
                ) : previewFile.type === 'pdf' ? (
                  <embed
                    src={blobUrl}
                    type="application/pdf"
                    className="w-full h-full border-0 bg-white shadow-lg"
                  />
                ) : null}
              </div>
            ) : null}
          </div>

          {/* Status Bar */}
          <div className="flex items-center justify-between px-4 py-2 bg-gray-100 border-t text-sm text-gray-600">
            <div className="flex items-center space-x-4">
              <span>Zoom: {zoom}%</span>
              <span>Rotație: {rotation}°</span>
              {blobUrl && !previewLoading && !previewError && (
                <span className="text-green-600 font-medium">✓ Fișier încărcat</span>
              )}
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default AdminUploadedDocumentsViewer;